import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleIcon } from '../ui/icons';

export default function AuthPage({ page, csrfToken }) {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const otpInputRefs = useRef([]);

    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo') || '/';
    const otpDigits = Array.from({ length: 4 }, (_, index) => otp[index] || '');

    const isDesktop = (page === 'signup/desktop');
    const isHeicToJpg = (page === 'signup/heictojpg');
    const isCampaignSignup = isDesktop || isHeicToJpg;
    const signupSource = isDesktop ? 'desktop' : isHeicToJpg ? 'heictojpg' : null;

    function updateOtpDigits(nextDigits) {
        setOtp(nextDigits.join('').slice(0, 4));
    }

    function focusOtpInput(index) {
        const targetInput = otpInputRefs.current[index];
        if (targetInput) {
            targetInput.focus();
            targetInput.select();
        }
    }

    function handleOtpChange(index, value) {
        const digits = value.replace(/\D/g, '');

        if (!digits) {
            const nextDigits = [...otpDigits];
            nextDigits[index] = '';
            updateOtpDigits(nextDigits);
            return;
        }

        const nextDigits = [...otpDigits];
        let nextIndex = index;

        digits.slice(0, 4 - index).split('').forEach((digit, offset) => {
            nextDigits[index + offset] = digit;
            nextIndex = index + offset;
        });

        updateOtpDigits(nextDigits);
        focusOtpInput(Math.min(nextIndex + 1, 3));
    }

    function handleOtpKeyDown(index, event) {
        if (event.key === 'Backspace') {
            event.preventDefault();

            if (otpDigits[index]) {
                const nextDigits = [...otpDigits];
                nextDigits[index] = '';
                updateOtpDigits(nextDigits);
                return;
            }

            if (index > 0) {
                const nextDigits = [...otpDigits];
                nextDigits[index - 1] = '';
                updateOtpDigits(nextDigits);
                focusOtpInput(index - 1);
            }
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            focusOtpInput(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < 3) {
            event.preventDefault();
            focusOtpInput(index + 1);
        }
    }

    function handleOtpPaste(event) {
        event.preventDefault();

        const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
        if (!pastedDigits) {
            return;
        }

        const nextDigits = Array.from({ length: 4 }, (_, index) => pastedDigits[index] || '');
        updateOtpDigits(nextDigits);
        focusOtpInput(Math.min(pastedDigits.length, 4) - 1);
    }

    async function handleResendCode() {
        setError('');
        setLoading(true);

        try {
            const res = await fetch('/auth/passwordless/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': csrfToken,
                },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to resend code');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    function handleBackToEmail() {
        setCodeSent(false);
        setOtp('');
        setError('');
    }

    async function handleSendCode(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await fetch('/auth/passwordless/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': csrfToken,
                },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to send code');
                setLoading(false);
                return;
            }

            setCodeSent(true);
            setLoading(false);
        } catch {
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    }

    async function handleVerifyCode(e) {
        e.preventDefault();
        setError('');

        if (otp.length !== 4) {
            setError('Please enter the 4-digit code');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch('/auth/passwordless/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': csrfToken,
                },
                body: JSON.stringify({ email, otp, signupSource }),
            });

            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Invalid verification code');
                setLoading(false);
                return;
            }

            window.location.href = data?.redirect || returnTo;
        } catch {
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    }

    useEffect(() => {
        mixpanel?.track('auth_started', { page, source: signupSource || 'login' });
    }, []);

    return (
        <div className='flex-1 h-full bg-[#111] lg:bg-[#070707] overflow-auto custom-scroll'>
            <div className='min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]'>
                <div
                    className='flex flex-col justify-center min-h-[480px] lg:min-h-0 px-7 py-[42px] lg:px-16 lg:py-14 bg-cover bg-center bg-no-repeat relative'
                    style={{ backgroundImage: isCampaignSignup ? 'url(/static/images/auth-bg2.jpg)' : 'url(/static/images/auth-bg1.jpg)' }}
                >
                    <div className='max-w-[600px]'>
                        {isDesktop ? (
                            <div className='inline-flex items-center gap-2 text-[13px] font-bold text-green1 tracking-[0.01em] px-3.5 py-2 bg-[#03d46e21] rounded-full border border-[#03d46e47]'>
                                <span className='w-1.5 h-1.5 rounded-full bg-green1 shadow-[0_0_8px_rgba(3, 212, 110, 0.7)]' />
                                Coming from JPEGmini Desktop
                            </div>
                        ) : (
                            <div className='flex items-center h-6'>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-green1/20 opacity-80 text-xs11 font-medium text-green1'>
                                    <img src='/static/images/icons/zap.svg' className='w-3 h-3 svg-green1' />
                                    {isHeicToJpg ? 'HEIC → JPG' : 'COMPRESSION'}
                                </div>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                    {isHeicToJpg ? 'BATCH CONVERT' : 'AI FRAMING'}
                                </div>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                    {isHeicToJpg ? '5 GB FREE STORAGE' : 'CLIENT DELIVERY'}
                                </div>
                            </div>
                        )}

                        <div className='mt-[22px] text-[56px] text-[#eef0f5] font-semibold leading-[normal] tracking-[1.2px]'>
                            {isDesktop ? (<>
                                Your videos,<br />
                                now in the <span className='text-[#13d67a]'>cloud.</span>
                            </>) : isHeicToJpg ? (<>
                                Convert <span className='text-[#13d67a]'>HEIC</span> to <span className='text-[#13d67a]'>JPG</span>.<br />
                                Free, Instant, Online.
                            </>) : (<>
                                Compress.<br />
                                Reframe.<br />
                                <span className='text-[#13d67a]'>Deliver.</span>
                            </>)}
                        </div>

                        <div className={`mt-4 ${isDesktop ? '' : isHeicToJpg ? 'w-[480px]' : 'w-[360px]'} text-[#eef0f5]/60 text-sm leading-[175%]`}>
                            {isDesktop ? (<>
                                JPEGmini Desktop handles your photos.
                                The Cloud handles your videos — much faster processing, AI reframing, and instant client delivery.
                            </>) : isHeicToJpg ? (<>
                                Convert unlimited HEICs — and keep them.
                                Your photos are stored, converted, and up to 80% smaller, ready whenever you need them.
                                No re-uploading, no file limits.
                            </>) : (<>
                                Compress photos and video with zero quality loss.
                                Store shoots online, reframe for every platform, and deliver with a single link — everything your clients need, nothing they don't.
                            </>)}
                        </div>

                        {isDesktop ? (<>
                            <div className='mt-8 grid gap-3.5'>
                                {[
                                    'Process videos in minutes — no more overnight exports',
                                    'Compress videos up to 50% smaller — same quality',
                                    'AI reframes 16:9 to 9:16 vertical in one click',
                                    'Send clients a single link — no re-uploading anywhere',
                                ].map((item, i) => (
                                    <div key={i} className='flex gap-3 items-start text-neutral-100 text-[15px] leading-[1.4]'>
                                        <span className='shrink-0 flex-center w-5 h-5 rounded-full bg-[#03d46e38] text-green1 text-xs11 font-black'>✓</span>
                                        {item}
                                    </div>
                                ))}
                            </div>

                            <div className='mt-9 inline-flex items-center flex-wrap gap-2.5 px-4 py-2.5 rounded-full bg-black/45 border border-white/12 text-[#f0f0f0] text-[13px] font-bold backdrop-blur-xs'>
                                Upload <span className='text-green1'>→</span>
                                Compress <span className='text-green1'>→</span>
                                Reframe <span className='text-green1'>→</span>
                                Deliver
                            </div>
                        </>) : (
                            <div className='mt-10 w-[410px] h-[69px] flex rounded-[14px] bg-[#0b0c0f]/55 border border-white/10 backdrop-filter-[blur(8px)]'>
                                <div className='flex flex-col justify-center items-start gap-1 px-5 border-r border-white/7'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>{isHeicToJpg ? 'CONVERT' : 'AVG SIZE SAVED'}</div>
                                    <div className='text-[#eef0f5]'>{
                                        isHeicToJpg ? 'HEIC → JPG'
                                        : <div className='text-xs'>Photos <span className='text-[#13d67a]'>-80%</span><br/>Video <span className='text-[#13d67a]'>-52%</span></div>
                                    }</div>
                                </div>
                                <div className='flex flex-col justify-center items-start gap-1 px-5'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>{isHeicToJpg ? 'OPTIMIZE' : 'AI REFRAME'}</div>
                                    <div className={isHeicToJpg ? 'text-[#13d67a]' : 'text-[#eef0f5]'}>{isHeicToJpg ? 'up to 80%' : '16:9 → 9:16'}</div>
                                </div>
                                <div className='flex flex-col justify-center items-start gap-1 px-5 border-l border-white/7'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>TRUSTED BY</div>
                                    <div className='text-[#eef0f5]'>50K creators</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='absolute right-7 lg:right-16 bottom-[42px] lg:bottom-10 text-white text-base font-semibold'>
                        Photo by: Fotis Sid Photography
                    </div>
                </div>

                <div className='bg-(--gray-darkest) flex-center px-[22px] py-10 lg:px-[34px] lg:py-12'>
                    <div className='w-full max-w-[360px]'>
                        <a href='/' title='JPEGmini Cloud' className='flex-center gap-3'>
                            <img src='/static/images/jpegmini-icon.png' className='w-14 h-14' />
                            <div className='flex items-end gap-2'>
                                <div className='font-bold text-white text-[22px] leading-[1]'>JPEGmini</div>
                                <div className='text-(--green-primary) text-lg font-semibold tracking-[0.6px] leading-[1]'>CLOUD</div>
                            </div>
                        </a>

                        {isDesktop && (
                            <div className='mt-8 px-4 py-3.5 rounded-xl bg-[#03d46e0f] border border-[#03d46e40] text-[#dcf8e9] text-[13px] leading-[1.5]'>
                                <div className='flex items-center gap-2 mb-1 text-green1 font-bold text-[13px]'>
                                    <span className='w-4 h-4 rounded-full bg-[#03d46e33] inline-flex justify-center items-center text-xs11 font-black'>i</span>
                                    New account needed
                                </div>
                                Cloud is a separate product from JPEGmini Desktop, so your desktop login won't work here. Takes 30 seconds to set up.
                            </div>
                        )}


                        <div className='mt-14 text-center text-white text-[18px] font-semibold leading-[1.4]'>
                            {codeSent
                                ? 'Enter the 4-digit code'
                                : isCampaignSignup
                                ? 'Create your free account'
                                : 'What\'s your email?'
                            }
                        </div>

                        <div className='mt-2 text-center text-white/55 text-sm leading-[140%]'>
                            {codeSent
                                ? <>sent to you at <span className='text-white/75'>{email}</span></>
                                : isCampaignSignup
                                ? isDesktop
                                    ? 'No credit card. No trial timer.'
                                    : 'Convert HEIC to JPG and unlock JPEGmini Cloud, one login.'
                                : ''
                            }
                        </div>

                        {isDesktop && !codeSent && (
                            <div className='mt-3.5 px-4 py-3.5 rounded-[10px] bg-white/3 border border-white/6'>
                                <div className='flex items-center justify-between mb-2.5'>
                                    <div className='px-2 py-0.5 rounded-full bg-[#03d46e26] text-green1 text-xs11 font-extrabold tracking-[0.06em]'>
                                        FREE PLAN
                                    </div>
                                    <div className='text-white text-[13px] font-bold'>$0 · forever free</div>
                                </div>
                                <div className='grid gap-[7px]'>
                                    {[
                                        '1 active project',
                                        '5 GB max storage size',
                                        'Unlimited compression',
                                        'Shareable links',
                                    ].map((item, i) => (
                                        <div key={i} className='flex items-center gap-2 text-[#d8d8d8] text-[13px] leading-[1.3]'>
                                            <div className='text-green1 font-black text-xs'>✓</div>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(error || searchParams.get('error')) && (
                            <div className='mt-8 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm'>
                                {error || 'Authentication failed. Please try again.'}
                            </div>
                        )}

                        <form className='mt-8' onSubmit={codeSent ? handleVerifyCode : handleSendCode}>
                            {codeSent ? (<>
                                <div className='flex items-center justify-center gap-2.5 sm:gap-3'>
                                    {otpDigits.map((digit, index) => {
                                        const isActive = digit;

                                        return (
                                            <input
                                                key={index}
                                                ref={element => {
                                                    otpInputRefs.current[index] = element;
                                                }}
                                                type='text'
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                                value={digit}
                                                onChange={event => handleOtpChange(index, event.target.value)}
                                                onKeyDown={event => handleOtpKeyDown(index, event)}
                                                onPaste={handleOtpPaste}
                                                className={`auth-otp-input ${isActive ? 'auth-otp-input-active' : ''}`}
                                                maxLength={1}
                                                required
                                                autoFocus={index === 0}
                                            />
                                        );
                                    })}
                                </div>

                                <div className='mt-4 text-center'>
                                    <button
                                        type='button'
                                        className='text-[10px] text-(--gray) font-medium underline hover:opacity-75 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed'
                                        disabled={loading}
                                        onClick={handleResendCode}
                                    >
                                        Resend code
                                    </button>
                                </div>

                                <div className='mt-12 flex items-center justify-between gap-3'>
                                    <button
                                        type='button'
                                        className='neutral-button w-20 h-12! justify-center text-sm!'
                                        disabled={loading}
                                        onClick={handleBackToEmail}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type='submit'
                                        className='action-button w-20 h-12! justify-center rounded-lg! text-sm!'
                                        disabled={loading || otp.length !== 4}
                                    >
                                        {loading ? <div className='spinner w-4 h-4 svg-gray2'></div> : 'Next'}
                                    </button>
                                </div>
                            </>) : (<>
                                <input
                                    type='email'
                                    name='email'
                                    placeholder='Email'
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className='auth-input'
                                    required
                                    autoFocus
                                    autoComplete='username'
                                />

                                <button
                                    type='submit'
                                    className='mt-4 action-button w-full h-[52px] rounded-lg! text-base!'
                                    disabled={loading}
                                >
                                    {loading ? 'Sending code...' : 'Continue'}
                                </button>

                                <div className='my-5 flex items-center gap-4'>
                                    <div className='flex-grow h-px bg-(--gray-dark)'></div>
                                    <span className='text-xs text-white'>OR</span>
                                    <div className='flex-grow h-px bg-(--gray-dark)'></div>
                                </div>

                                <a
                                    href={`/auth/google?returnTo=${encodeURIComponent(returnTo)}${signupSource ? `&signupSource=${encodeURIComponent(signupSource)}` : ''}`}
                                    className='neutral-button2 w-full h-[48px]! justify-center gap-3!'
                                >
                                    <GoogleIcon />
                                    {isCampaignSignup ? 'Sign up with Google' : 'Continue with Google'}
                                </a>

                                {isCampaignSignup && (
                                    <div className='mt-4.5 text-center text-[13px] text-[#8a8a8a]'>
                                        Already have an account?{' '}
                                        <a href={`/login?returnTo=${encodeURIComponent(returnTo)}`} className='text-green1 hover:text-green2 font-medium'>
                                            Log in
                                        </a>
                                    </div>
                                )}

                                <div className='mt-7 text-center text-[10px] font-medium text-(--gray) leading-[1.4]'>
                                    By creating an account, you agree to our{' '}
                                    <a href='/terms' target='_blank' className='underline hover:opacity-80'>Terms of Service</a>{' '}
                                    and{' '}
                                    <a href='/privacy' target='_blank' className='underline hover:opacity-80'>Privacy Policy</a>,{' '}
                                    and consent to receive verification codes via email
                                </div>

                                <div className='mt-7 flex items-center justify-between text-(--gray) text-[10px] font-medium'>
                                    <div className=''>Trusted by 50,000+ creators</div>
                                    <div className='flex items-center gap-1.5'>
                                        <div className='text-(--yellow)'>★★★★★</div>
                                        <div className=''>4.8 TrustPilot</div>
                                    </div>
                                </div>
                            </>)}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
